import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    subDays,
    subMonths,
    startOfYear,
    format,
    parseISO,
    isValid,
} from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DateRange {
    startDate: Date;
    endDate: Date;
}

export type PresetKey = '30D' | '90D' | '6M' | 'YTD';

interface DateRangeFilterProps {
    value: DateRange;
    onChange: (range: DateRange) => void;
}

// ─── Preset helpers ───────────────────────────────────────────────────────────

const PRESETS: { key: PresetKey; label: string; getRange: () => DateRange }[] = [
    {
        key: '30D',
        label: '30D',
        getRange: () => ({ startDate: subDays(new Date(), 30), endDate: new Date() }),
    },
    {
        key: '90D',
        label: '90D',
        getRange: () => ({ startDate: subDays(new Date(), 90), endDate: new Date() }),
    },
    {
        key: '6M',
        label: '6M',
        getRange: () => ({ startDate: subMonths(new Date(), 6), endDate: new Date() }),
    },
    {
        key: 'YTD',
        label: 'YTD',
        getRange: () => ({ startDate: startOfYear(new Date()), endDate: new Date() }),
    },
];

const toInputValue = (d: Date) => format(d, 'yyyy-MM-dd');

const detectActivePreset = (range: DateRange): PresetKey | null => {
    const now = new Date();
    const start = range.startDate.toDateString();
    for (const p of PRESETS) {
        if (p.getRange().startDate.toDateString() === start && range.endDate.toDateString() === now.toDateString()) {
            return p.key;
        }
    }
    return null;
};

// ─── Component ────────────────────────────────────────────────────────────────

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ value, onChange }) => {
    const [draftStart, setDraftStart] = useState(toInputValue(value.startDate));
    const [draftEnd, setDraftEnd] = useState(toInputValue(value.endDate));
    const activePreset = detectActivePreset(value);

    const applyPreset = (preset: typeof PRESETS[number]) => {
        const range = preset.getRange();
        setDraftStart(toInputValue(range.startDate));
        setDraftEnd(toInputValue(range.endDate));
        onChange(range);
    };

    const applyCustom = () => {
        const s = parseISO(draftStart);
        const e = parseISO(draftEnd);
        if (isValid(s) && isValid(e) && s <= e) {
            onChange({ startDate: s, endDate: e });
        }
    };

    return (
        <div className="flex flex-wrap items-end gap-3 p-4 bg-card border rounded-lg shadow-sm">
            {/* Presets */}
            <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground mr-1">Quick:</span>
                {PRESETS.map((p) => (
                    <Button
                        key={p.key}
                        variant={activePreset === p.key ? 'default' : 'outline'}
                        size="sm"
                        className="h-8 px-3 text-xs"
                        onClick={() => applyPreset(p)}
                    >
                        {p.label}
                    </Button>
                ))}
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-border hidden sm:block" />

            {/* Custom range */}
            <div className="flex items-end gap-2">
                <div className="grid gap-1">
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input
                        type="date"
                        className="h-8 text-xs w-36"
                        value={draftStart}
                        max={draftEnd}
                        onChange={(e) => setDraftStart(e.target.value)}
                    />
                </div>
                <div className="grid gap-1">
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input
                        type="date"
                        className="h-8 text-xs w-36"
                        value={draftEnd}
                        min={draftStart}
                        max={toInputValue(new Date())}
                        onChange={(e) => setDraftEnd(e.target.value)}
                    />
                </div>
                <Button
                    size="sm"
                    className="h-8 px-4 text-xs"
                    onClick={applyCustom}
                    disabled={!draftStart || !draftEnd || draftStart > draftEnd}
                >
                    Apply
                </Button>
            </div>

            {/* Active range label */}
            <div className="ml-auto text-xs text-muted-foreground hidden lg:block">
                Showing:{' '}
                <span className="font-medium text-foreground">
                    {format(value.startDate, 'dd MMM yyyy')} — {format(value.endDate, 'dd MMM yyyy')}
                </span>
            </div>
        </div>
    );
};

export default DateRangeFilter;
export { PRESETS };
